import React from "react";
import { motion } from "framer-motion";
import { SITE } from "@/lib/site";
import { Mail, MessageCircle, MapPin, ArrowUpRight } from "lucide-react";

const Contact = () => {
  const waUrl = `https://wa.me/${SITE.phoneClean}?text=${encodeURIComponent(SITE.whatsappMessage)}`;
  const mailBody = `Namaste Archana,

I'd like to know more about your healing sessions.

Name:
Service of interest:
Preferred time:

Thank you.`;
  const mailUrl = `mailto:${SITE.email}?subject=${encodeURIComponent("Healing session enquiry — Aura Bliss Reiki")}&body=${encodeURIComponent(mailBody)}`;

  return (
    <section id="contact" data-testid="contact-section" className="py-24 lg:py-32 bg-[#2C302E] text-[#FAF9F6] relative overflow-hidden">
      <div className="pointer-events-none absolute -top-32 -right-32 h-[28rem] w-[28rem] rounded-full bg-[#C5A880]/15 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 -left-32 h-[28rem] w-[28rem] rounded-full bg-[#8A9A86]/10 blur-3xl" />

      <div className="mx-auto max-w-6xl px-6 lg:px-12 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center max-w-2xl mx-auto"
        >
          <div className="text-xs uppercase tracking-[0.28em] text-[#C5A880]">— Reach out</div>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[1.05]">
            Let's begin a<br />gentle conversation.
          </h2>
          <p className="mt-7 text-[#FAF9F6]/70 leading-relaxed text-lg">
            Send a quick message on WhatsApp or write me an email — I read every
            note myself and reply within 24 hours.
          </p>
        </motion.div>

        <div className="mt-16 grid md:grid-cols-2 gap-5 lg:gap-7">
          <motion.a
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-cta-whatsapp"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="group relative rounded-3xl bg-[#FAF9F6] text-[#2C302E] p-8 lg:p-10 hover:-translate-y-1 transition-transform"
          >
            <div className="flex items-start justify-between">
              <span className="h-14 w-14 rounded-2xl bg-[#25D366]/15 flex items-center justify-center">
                <MessageCircle className="h-6 w-6 text-[#25D366]" strokeWidth={2} />
              </span>
              <ArrowUpRight className="h-5 w-5 text-[#2C302E]/40 group-hover:text-[#B35B47] transition-colors" />
            </div>
            <div className="mt-7 text-xs uppercase tracking-[0.22em] text-[#8A8D86]">WhatsApp · Fastest</div>
            <h3 className="font-serif text-3xl lg:text-4xl mt-2">Chat with Archana</h3>
            <div data-testid="contact-phone" className="mt-3 text-[#5C605A] text-lg">{SITE.phone}</div>
            <p className="mt-4 text-sm text-[#5C605A] leading-relaxed">
              Tap to open WhatsApp with a pre-filled greeting. Best for quick
              questions and booking slots.
            </p>
          </motion.a>

          <motion.a
            href={mailUrl}
            data-testid="contact-cta-email"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="group relative rounded-3xl bg-[#FAF9F6] text-[#2C302E] p-8 lg:p-10 hover:-translate-y-1 transition-transform"
          >
            <div className="flex items-start justify-between">
              <span className="h-14 w-14 rounded-2xl bg-[#B35B47]/15 flex items-center justify-center">
                <Mail className="h-6 w-6 text-[#B35B47]" strokeWidth={2} />
              </span>
              <ArrowUpRight className="h-5 w-5 text-[#2C302E]/40 group-hover:text-[#B35B47] transition-colors" />
            </div>
            <div className="mt-7 text-xs uppercase tracking-[0.22em] text-[#8A8D86]">Email · Thoughtful</div>
            <h3 className="font-serif text-3xl lg:text-4xl mt-2">Write a longer note</h3>
            <div data-testid="contact-email" className="mt-3 text-[#5C605A] text-lg break-all">{SITE.email}</div>
            <p className="mt-4 text-sm text-[#5C605A] leading-relaxed">
              Best for sharing more context about what's calling for healing.
              I'll reply within 24 hours.
            </p>
          </motion.a>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="mt-10 flex items-center justify-center gap-3 text-sm text-[#FAF9F6]/60"
        >
          <MapPin className="h-4 w-4 text-[#C5A880]" />
          {SITE.location}
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;
